import React, { Fragment, useEffect, useState } from 'react';
import { Button, Row, Col, Container, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';

import {
  requestInsert,
  requestUpdate,
  requestInform as request
} from 'api/standard/code/product';
import { requestList } from 'api/category';
import Search from 'components/common-sfims/form/Search';
import Select from 'components/common-sfims/form/Select';
import Modal from 'components/common-sfims/modal/Common';
import { array2ValueAndText } from 'function/convert';
import { onEnterKeyPress, onInputChange } from 'function/event';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import { RETURN_YN_SELECT } from 'types/type';

/**
 * 기준 정보 > 제품 코드 - 추가/수정
 */
const Setting = ({
  show,
  feedFormCds,
  mtrilGrpCds,
  packageUnitCds,
  salesKgCds,
  seq,
  sunjinStageCds,
  onHide,
  onSetting
}) => {
  /* variable */
  const { fatoryCodeSeq } = getLoginInform();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();
  const {
    plant: { hasMods: hasModPlants }
  } = usePlantState();

  /* state */
  // 외부자재 그룹명 검색어
  const [extMtrilGrpCd, setExtMtrilGrpCd] = useState('');
  // 외부자재 그룹명 목록
  const [extMtrilGrpCds, setExtMtrilGrpCds] = useState([]);
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // 제품 코드 추가/수정 요청 데이터
  const [productCodeData, setProductCodeData] = useObjReducer({});
  // 사육 단계 검색어
  const [sunjinStageCd, setSunjinStageCd] = useState('');

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우
    if (!show) {
      return;
    }

    // 데이터 초기화
    setExtMtrilGrpCd('');
    setProductCodeData({
      factoryCodeSeq: fatoryCodeSeq,
      prdtCd: '',
      prdtNm: '',
      mtrilGrpCd: '',
      sunjinStageCd: '',
      feedFormCd: '',
      packageUnitCd: '',
      salesKgCd: '',
      useYn: '',
      extMtrilGrpCd: ''
    });
    setSunjinStageCd('');

    // 수정 화면인 경우 제품 코드 정보 조회 요청
    if (seq) {
      requestInform();
    }
  }, [show]);
  useEffect(() => {
    setProductCodeData({
      extMtrilGrpCd:
        extMtrilGrpCds.find(({ ctgryNmKr }) => ctgryNmKr === extMtrilGrpCd)
          ?.ctgryCd ?? ''
    });
  }, [extMtrilGrpCd]);
  useEffect(() => {
    setExtMtrilGrpCd(
      extMtrilGrpCds.find(
        ({ ctgryCd }) => ctgryCd === productCodeData.extMtrilGrpCd
      )?.ctgryNmKr ?? ''
    );
  }, [extMtrilGrpCds]);
  useEffect(() => {
    // 축종 선택 시 해당 축종에 속한 외부자재 그룹명 목록 조회 요청
    if (productCodeData.mtrilGrpCd) {
      requestExtMtrilGrpCd();
    }
  }, [productCodeData.mtrilGrpCd]);
  useEffect(() => {
    setProductCodeData({
      sunjinStageCd:
        sunjinStageCds.find(({ codeNmKr }) => codeNmKr === sunjinStageCd)
          ?.codeCd ?? ''
    });
  }, [sunjinStageCd]);

  /* method */
  // 외부자재 그룹명 목록 조회 요청
  const requestExtMtrilGrpCd = async () => {
    const { list = [] } = await requestList({
      ctgryGroupCd: productCodeData.mtrilGrpCd
    });

    setExtMtrilGrpCds(list);
  };
  // 제품 코드 정보 조회 요청
  const requestInform = async () => {
    try {
      const { data = {}, code, message } = await request(seq);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setProductCodeData({
        factoryCodeSeq: data.factoryCodeSeq ?? fatoryCodeSeq,
        prdtCd: data.prdtCd ?? '',
        prdtNm: data.prdtNm ?? '',
        mtrilGrpCd: data.mtrilGrpCd ?? '',
        sunjinStageCd: data.sunjinStageCd ?? '',
        feedFormCd: data.feedFormCd ?? '',
        packageUnitCd: data.packageUnitCd ?? '',
        salesKgCd: data.salesKgCd ?? '',
        useYn: data.useYn ?? '',
        extMtrilGrpCd: data.extMtrilGrpCd ?? ''
      });
      setSunjinStageCd(data.sunjinStageNm ?? '');
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });

      // 모달 숨김
      onHide();
    }
  };

  /* event */
  // 설정
  const onSubmit = async () => {
    if (isDisableSetting) {
      return;
    }

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (
        !isValidRequired(productCodeData, ['sunjinStageCd', 'extMtrilGrpCd'])
      ) {
        throw {
          code: RESPONSE_CODE[seq ? 'BadRequestUpdate' : 'BadRequestInsert']
        };
      }

      if (seq) {
        const { code, message } = await requestUpdate(seq, productCodeData);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      } else {
        const { code, message } = await requestInsert(productCodeData);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      }

      // 설정 시 이벤트 실행
      onSetting();

      // 모달 숨김
      onHide();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableSetting(false);
    }
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{ footer: 'align-items-center justify-content-center' }}
      size="lg"
      onHide={onHide}
    >
      <Fragment key="header">
        {getText(seq ? 'updateProductCode' : 'insertProductCode')}
      </Fragment>
      <Fragment key="body">
        <Container>
          <Row className="mb-3">
            <Col sm={6}>
              <Form.Group>
                <Form.Label>* {getText('pltCode')}</Form.Label>
                <Select
                  datas={array2ValueAndText(hasModPlants, 'seq', 'pltCode')}
                  placeholder="pltCode"
                  value={productCodeData.factoryCodeSeq}
                  className="w-100"
                  name="factoryCodeSeq"
                  onChange={event => onInputChange(event, setProductCodeData)}
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>
                  {!seq && '* '}
                  {getText('prdtCd')}
                </Form.Label>
                <Form.Control
                  readOnly={seq}
                  size="sm"
                  type="text"
                  value={productCodeData.prdtCd}
                  autoFocus={!seq}
                  name="prdtCd"
                  onChange={event => onInputChange(event, setProductCodeData)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col sm={12}>
              <Form.Group>
                <Form.Label>* {getText('prdtNm')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={productCodeData.prdtNm}
                  autoFocus={seq}
                  name="prdtNm"
                  onChange={event => onInputChange(event, setProductCodeData)}
                  onKeyUp={event => onEnterKeyPress(event, onSubmit)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col sm={6}>
              <Form.Group>
                <Form.Label>* {getText('species')}</Form.Label>
                <Select
                  datas={array2ValueAndText(mtrilGrpCds, 'codeCd', 'codeNmKr')}
                  placeholder="species"
                  value={productCodeData.mtrilGrpCd}
                  className="w-100"
                  name="mtrilGrpCd"
                  onChange={event => onInputChange(event, setProductCodeData)}
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>{getText('sunjinStageNm')}</Form.Label>
                <Search
                  datas={array2ValueAndText(sunjinStageCds, 'codeNmKr')}
                  value={sunjinStageCd}
                  setValue={setSunjinStageCd}
                  onKeyUp={event => onEnterKeyPress(event, onSubmit)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col sm={6}>
              <Form.Group>
                <Form.Label>* {getText('feedFormNm')}</Form.Label>
                <Select
                  datas={array2ValueAndText(feedFormCds, 'codeCd', 'codeNmKr')}
                  placeholder="feedFormNm"
                  value={productCodeData.feedFormCd}
                  className="w-100"
                  name="feedFormCd"
                  onChange={event => onInputChange(event, setProductCodeData)}
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>* {getText('packageUnitNm')}</Form.Label>
                <Select
                  datas={array2ValueAndText(
                    packageUnitCds,
                    'codeCd',
                    'codeNmKr'
                  )}
                  placeholder="packageUnitNm"
                  value={productCodeData.packageUnitCd}
                  className="w-100"
                  name="packageUnitCd"
                  onChange={event => onInputChange(event, setProductCodeData)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col sm={6}>
              <Form.Group>
                <Form.Label>* {getText('salesKgNm')}</Form.Label>
                <Select
                  datas={array2ValueAndText(salesKgCds, 'codeCd', 'codeNmKr')}
                  placeholder="salesKgNm"
                  value={productCodeData.salesKgCd}
                  className="w-100"
                  name="salesKgCd"
                  onChange={event => onInputChange(event, setProductCodeData)}
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>* {getText('useYn')}</Form.Label>
                <Select
                  datas={array2ValueAndText(RETURN_YN_SELECT, 'key', 'value')}
                  placeholder="useYn"
                  value={productCodeData.useYn}
                  className="w-100"
                  name="useYn"
                  onChange={event => onInputChange(event, setProductCodeData)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col sm={12}>
              <Form.Group>
                <Form.Label>{getText('extMtrilGrpNm')}</Form.Label>
                <Search
                  datas={array2ValueAndText(extMtrilGrpCds, 'ctgryNmKr')}
                  placeholder={!productCodeData.mtrilGrpCd && 'selectSpecies'}
                  readOnly={!productCodeData.mtrilGrpCd}
                  value={extMtrilGrpCd}
                  setValue={setExtMtrilGrpCd}
                  onKeyUp={event => onEnterKeyPress(event, onSubmit)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Container>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText('cancel')}
        </Button>
        <Button disabled={isDisableSetting} className="ms-2" onClick={onSubmit}>
          {getText('ok')}
        </Button>
      </Fragment>
    </Modal>
  );
};

Setting.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 성형 목록
  feedFormCds: PropTypes.arrayOf(PropTypes.object),

  // 축종 목록
  mtrilGrpCds: PropTypes.arrayOf(PropTypes.object),

  // 포장 형태 목록
  packageUnitCds: PropTypes.arrayOf(PropTypes.object),

  // 판매 단위 목록
  salesKgCds: PropTypes.arrayOf(PropTypes.object),

  // 제품 코드 번호
  seq: PropTypes.number,

  // 사육 단계 목록
  sunjinStageCds: PropTypes.arrayOf(PropTypes.object),

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 설정 시 이벤트
  onSetting: PropTypes.func
};
Setting.defaultProps = {
  show: false,
  feedFormCds: [],
  mtrilGrpCds: [],
  packageUnitCds: [],
  salesKgCds: [],
  sunjinStageCds: []
};

export default Setting;
